/**
 * Formelanalyse.
 *
 * Der zweite rote Faden des Kurses lautet: Excel rechnet, du tippst nicht. Ein
 * Wert ist eine Zahl oder ein Datum, kein Text mit angehängter Einheit; eine
 * Formel enthält Bezüge, keine getippten Konstanten.
 *
 * Prüfen lässt sich das über `Range.formulas`: Office.js liefert die Formel als
 * Zeichenkette, so wie sie in der Bearbeitungsleiste steht. Die Funktionen hier
 * arbeiten deshalb rein auf Zeichenketten und brauchen kein Office.js – sie
 * lassen sich damit auch außerhalb von Excel testen.
 *
 * Eine Einschränkung ist wichtig und wird in den Rückmeldungen benannt:
 * Office.js gibt Formeln in der Sprache der Excel-Oberfläche zurück. Eine
 * deutsche Excel-Version meldet `=SUMME(A1:A3)`, eine englische `=SUM(A1:A3)`.
 * Funktionsnamen werden deshalb immer gegen beide Schreibweisen geprüft.
 */

/** Zellbezug innerhalb einer Formel, z. B. "$B$1" oder "Tabelle2!A5". */
export interface Bezug {
  /** Der Bezug so, wie er in der Formel steht. */
  text: string;
  /** Blattname, wenn der Bezug blattübergreifend ist. */
  sheet: string | null;
  /** Ist die Spalte mit $ festgehalten? */
  columnAbsolute: boolean;
  /** Ist die Zeile mit $ festgehalten? */
  rowAbsolute: boolean;
}

/**
 * Textbausteine einer Formel entfernen.
 *
 * In `="Summe: "&B1` steht in Anführungszeichen alles Mögliche, auch Ziffern.
 * Ohne dieses Entfernen würde jede Formel mit Text als „Zahl fest eingetippt"
 * gelten.
 */
function ohneTextbausteine(formula: string): string {
  return formula.replace(/"[^"]*"/g, '""');
}

/**
 * Findet alle Zellbezüge einer Formel.
 *
 * Bewusst nicht erfasst werden Funktionsnamen und Zahlen; ein Bezug braucht
 * immer mindestens einen Buchstaben gefolgt von mindestens einer Ziffer.
 */
export function bezuege(formula: string): Bezug[] {
  const bereinigt = ohneTextbausteine(formula);
  const muster = /(?:'([^']+)'|([A-Za-zÄÖÜäöüß_][\wÄÖÜäöüß.]*))?!?(\$?)([A-Z]{1,3})(\$?)(\d{1,7})\b/g;
  const treffer: Bezug[] = [];

  for (const match of bereinigt.matchAll(muster)) {
    const [ganzes, quoted, plain, spalteAbsolut, , zeileAbsolut] = match;
    // Ein Blattname steht nur dann davor, wenn tatsächlich ein "!" folgt.
    const hatBlatt = ganzes.includes("!");
    treffer.push({
      text: ganzes,
      sheet: hatBlatt ? (quoted ?? plain ?? null) : null,
      columnAbsolute: spalteAbsolut === "$",
      rowAbsolute: zeileAbsolut === "$",
    });
  }
  return treffer;
}

/** Enthält die Formel überhaupt einen Zellbezug? */
export function hatBezug(formula: string): boolean {
  return bezuege(formula).length > 0;
}

/** Enthält die Formel einen Bezug auf ein anderes Blatt? */
export function hatBlattuebergreifendenBezug(formula: string): boolean {
  return bezuege(formula).some((bezug) => bezug.sheet !== null);
}

/** Enthält die Formel einen mit $ festgehaltenen (absoluten) Bezug? */
export function hatAbsolutenBezug(formula: string): boolean {
  return bezuege(formula).some((bezug) => bezug.columnAbsolute && bezug.rowAbsolute);
}

/**
 * Findet Zahlen, die direkt in der Formel stehen, statt aus einer Zelle zu kommen.
 *
 * Genau das ist der häufigste Anfängerfehler: `=B7*1,19` statt `=B7*$B$1`. Ändert
 * sich der Steuersatz, muss jede einzelne Formel angefasst werden.
 *
 * Unterschieden wird nach der Rolle, die die Zahl in der Formel spielt:
 *
 * - Eine Zahl, die für sich allein ein Argument einer Funktion ist, ist ein
 *   Bedienparameter und damit in Ordnung – die Stellenangabe in `RUNDEN(A1;2)`,
 *   die Spaltennummer in `SVERWEIS(A1;B:D;3;0)`, die Funktionsnummer in
 *   `TEILERGEBNIS(109;…)`.
 * - Eine Zahl, die in einer Rechnung steht, ist eine fest eingetippte Konstante:
 *   `=B7*1,19`, `=A1+100`, `=SUMME(A1:A9)/12`.
 *
 * Die Werte 0 und 1 werden nie gemeldet; sie kommen in Vergleichen und
 * WENN-Zweigen ganz normal vor.
 *
 * Der Sonderfall `=SUMME(100;200)` – Konstanten als eigenständige Argumente –
 * fällt damit durchs Raster. Er kommt in der Praxis kaum vor, und die Regel
 * bleibt dafür überschaubar.
 */
export function festeZahlen(formula: string): string[] {
  let bereinigt = ohneTextbausteine(formula);

  // Zellbezüge und Bereiche herauslösen, damit ihre Ziffern nicht mitzählen.
  for (const bezug of bezuege(bereinigt)) {
    bereinigt = bereinigt.replace(bezug.text, "#");
  }
  // Strukturierte Verweise wie Umsatz[Betrag] zuerst, danach die verbliebenen
  // Funktions- und Bereichsnamen. Beide dürfen keine Zahlen beisteuern.
  bereinigt = bereinigt.replace(/\[[^\]]*\]/g, "#");
  bereinigt = bereinigt.replace(/\b[A-Za-zÄÖÜäöüß_][\wÄÖÜäöüß.]*\b/g, "#");

  const treffer: string[] = [];
  const muster = /\d+(?:[.,]\d+)?/g;

  for (const match of bereinigt.matchAll(muster)) {
    const zahl = match[0];
    if (Number(zahl.replace(",", ".")) === 0 || Number(zahl.replace(",", ".")) === 1) continue;

    // Steht die Zahl für sich allein als Argument einer Funktion?
    const davor = bereinigt.slice(0, match.index).replace(/\s+$/, "").slice(-1);
    const danach = bereinigt.slice(match.index + zahl.length).replace(/^\s+/, "").slice(0, 1);
    const istArgument = (davor === "(" || davor === ";" || davor === ",") && (danach === ")" || danach === ";" || danach === ",");
    if (istArgument) continue;

    treffer.push(zahl);
  }
  return treffer;
}

/** Steckt in der Formel eine fest eingetippte Zahl? */
export function hatFesteZahl(formula: string): boolean {
  return festeZahlen(formula).length > 0;
}

/**
 * Deutsche und englische Schreibweise der Funktionen, die im Kurs vorkommen.
 *
 * Office.js liefert Formeln in der Sprache der Excel-Oberfläche. Geprüft wird
 * deshalb immer gegen beide Namen.
 */
const FUNKTIONSNAMEN: Record<string, string[]> = {
  summe: ["SUMME", "SUM"],
  mittelwert: ["MITTELWERT", "AVERAGE"],
  anzahl: ["ANZAHL", "COUNT"],
  anzahl2: ["ANZAHL2", "COUNTA"],
  min: ["MIN"],
  max: ["MAX"],
  wenn: ["WENN", "IF"],
  wennfehler: ["WENNFEHLER", "IFERROR"],
  summewenn: ["SUMMEWENN", "SUMIF"],
  summewenns: ["SUMMEWENNS", "SUMIFS"],
  zaehlenwenn: ["ZÄHLENWENN", "COUNTIF"],
  zaehlenwenns: ["ZÄHLENWENNS", "COUNTIFS"],
  sverweis: ["SVERWEIS", "VLOOKUP"],
  xverweis: ["XVERWEIS", "XLOOKUP"],
  index: ["INDEX"],
  vergleich: ["VERGLEICH", "MATCH"],
  xvergleich: ["XVERGLEICH", "XMATCH"],
  runden: ["RUNDEN", "ROUND"],
  heute: ["HEUTE", "TODAY"],
  jetzt: ["JETZT", "NOW"],
  verketten: ["TEXTKETTE", "TEXTJOIN", "VERKETTEN", "CONCAT", "CONCATENATE"],
  teilergebnis: ["TEILERGEBNIS", "SUBTOTAL"],
  und: ["UND", "AND"],
  oder: ["ODER", "OR"],
  // Text
  links: ["LINKS", "LEFT"],
  rechts: ["RECHTS", "RIGHT"],
  teil: ["TEIL", "MID"],
  laenge: ["LÄNGE", "LEN"],
  finden: ["FINDEN", "FIND"],
  suchen: ["SUCHEN", "SEARCH"],
  textvor: ["TEXTVOR", "TEXTBEFORE"],
  textnach: ["TEXTNACH", "TEXTAFTER"],
  textteilen: ["TEXTTEILEN", "TEXTSPLIT"],
  glaetten: ["GLÄTTEN", "TRIM"],
  saeubern: ["SÄUBERN", "CLEAN"],
  gross2: ["GROSS2", "PROPER"],
  textverketten: ["TEXTVERKETTEN", "TEXTJOIN"],
  // Datum
  jahr: ["JAHR", "YEAR"],
  datedif: ["DATEDIF"],
  brteiljahre: ["BRTEILJAHRE", "YEARFRAC"],
  nettoarbeitstage: ["NETTOARBEITSTAGE", "NETWORKDAYS", "NETTOARBEITSTAGE.INTL", "NETWORKDAYS.INTL"],
  arbeitstag: ["ARBEITSTAG", "WORKDAY", "ARBEITSTAG.INTL", "WORKDAY.INTL"],
  edatum: ["EDATUM", "EDATE"],
  monatsende: ["MONATSENDE", "EOMONTH"],
  // Logik und bedingte Auswertung
  wenns: ["WENNS", "IFS"],
  ersterwert: ["ERSTERWERT", "SWITCH"],
  mittelwertwenns: ["MITTELWERTWENNS", "AVERAGEIFS"],
  // Dynamische Arrays
  filter: ["FILTER"],
  eindeutig: ["EINDEUTIG", "UNIQUE"],
  sortieren: ["SORTIEREN", "SORT", "SORTIERENNACH", "SORTBY"],
  let: ["LET"],
  // Was-wäre-wenn
  rmz: ["RMZ", "PMT"],
  mehrfachoperation: ["MEHRFACHOPERATION", "TABLE"],
  prognose: ["PROGNOSE.ETS", "FORECAST.ETS", "PROGNOSE.LINEAR", "FORECAST.LINEAR"],
};

/** Alle bekannten Schreibweisen einer Funktion, deutsch und englisch. */
export function funktionsnamen(schluessel: string): string[] {
  return FUNKTIONSNAMEN[schluessel] ?? [schluessel.toUpperCase()];
}

/**
 * Benutzt die Formel diese Funktion?
 *
 * @param schluessel Kleingeschriebener Bezeichner aus der Tabelle oben,
 *                   z. B. "summe" oder "sverweis".
 */
export function nutztFunktion(formula: string, schluessel: string): boolean {
  // Neuere Funktionen tragen in manchen Excel-Versionen ein Präfix wie
  // "_xlfn." oder "_xlfn._xlws." – es gehört nicht zum Namen.
  const bereinigt = ohneTextbausteine(formula)
    .toUpperCase()
    .replace(/_XL(FN|WS|PM)\./g, "");
  return funktionsnamen(schluessel).some((name) =>
    new RegExp(`(^|[^A-ZÄÖÜ0-9_.])${name.replace(/\./g, "\\.")}\\s*\\(`, "u").test(bereinigt),
  );
}

/** Benutzt die Formel mindestens eine der genannten Funktionen? */
export function nutztEineFunktion(formula: string, schluessel: string[]): boolean {
  return schluessel.some((eintrag) => nutztFunktion(formula, eintrag));
}

/**
 * Enthält die Formel einen strukturierten Verweis auf eine Tabelle?
 *
 * Strukturierte Verweise sehen so aus: `=SUMME(Umsatz[Betrag])`. Sie sind der
 * Grund, warum sich eine Tabelle beim Anfügen neuer Zeilen selbst mitrechnet.
 */
export function hatStrukturiertenVerweis(formula: string): boolean {
  return /[A-Za-zÄÖÜäöüß_][\wÄÖÜäöüß.]*\[/.test(ohneTextbausteine(formula));
}

/**
 * Ist der Zellinhalt eine Zahl, der jemand eine Einheit angehängt hat?
 *
 * "1200 €", "15 kg", "20%" als Text – solche Zellen sehen richtig aus, lassen
 * sich aber nicht addieren. Die Einheit gehört ins Zahlenformat, nicht in den
 * Wert.
 */
export function istZahlMitEinheit(wert: string): boolean {
  const bereinigt = wert.trim();
  if (bereinigt.length === 0) return false;
  return /^[+-]?\d{1,3}(?:[.\s]\d{3})*(?:,\d+)?\s*[^\d\s,.]{1,12}$/u.test(bereinigt) ||
    /^[+-]?\d+(?:[.,]\d+)?\s*[^\d\s,.]{1,12}$/u.test(bereinigt);
}

/**
 * Verweist die Formel auf diese Zelladresse?
 *
 * Vergleicht ohne Rücksicht auf Dollarzeichen: „$B$3", „B$3" und „B3" gelten
 * als derselbe Bezug, denn ob jemand den Bezug festgehalten hat, ist hier
 * nicht die Frage. Ein Blattname davor stört nicht, muss aber auch nicht
 * stimmen – wer das braucht, prüft `bezuege()` selbst aus.
 */
export function verweistAuf(formula: string, address: string): boolean {
  const gesucht = address.replace(/\$/g, "").toUpperCase();
  return bezuege(formula).some((bezug) => {
    const ohneBlatt = bezug.text.slice(bezug.text.lastIndexOf("!") + 1);
    return ohneBlatt.replace(/\$/g, "").toUpperCase() === gesucht;
  });
}

/** Verweist die Formel auf mindestens eine der genannten Adressen? */
export function verweistAufEine(formula: string, addresses: Iterable<string>): boolean {
  return [...addresses].some((adresse) => verweistAuf(formula, adresse));
}

/**
 * Gerüst einer Formel: alle Zeilennummern und Spaltenbuchstaben der Bezüge
 * durch Platzhalter ersetzt.
 *
 * Damit lässt sich erkennen, ob eine Formel nach unten ausgefüllt wurde: Alle
 * Zellen einer ausgefüllten Spalte tragen dasselbe Gerüst, obwohl sie
 * unterschiedliche Bezüge haben. Wer dagegen jede Zeile einzeln tippt, erzeugt
 * über kurz oder lang eine Abweichung – und genau die ist der Fehler, den man
 * in fremden Tabellen sucht.
 */
export function formelGeruest(formula: string): string {
  let geruest = ohneTextbausteine(formula);
  for (const bezug of bezuege(geruest)) {
    const platzhalter = `${bezug.columnAbsolute ? "$" : ""}#${bezug.rowAbsolute ? "$" : ""}#`;
    geruest = geruest.replace(bezug.text, bezug.sheet ? `${bezug.sheet}!${platzhalter}` : platzhalter);
  }
  return geruest.replace(/\s+/g, "").toUpperCase();
}

/**
 * Zerlegt den ersten Aufruf einer Funktion in seine Argumente.
 *
 * Nötig, weil sich Argumente nicht einfach an den Trennzeichen abzählen
 * lassen: Verschachtelte Funktionen bringen eigene mit, Text in
 * Anführungszeichen darf beliebige Zeichen enthalten – und Office.js liefert
 * die Formel in der Sprache der Oberfläche, also mit Semikolon im deutschen
 * und mit Komma im englischen Excel. Beide Trennzeichen gelten deshalb, aber
 * nur auf der äußersten Klammerebene des gesuchten Aufrufs.
 *
 * Gibt `null` zurück, wenn die Funktion in der Formel nicht vorkommt.
 */
export function funktionsArgumente(roh: string, schluessel: string): string[] | null {
  const formula = roh.replace(/_xl(fn|ws|pm)\./gi, "");
  const gross = formula.toUpperCase();

  for (const name of funktionsnamen(schluessel)) {
    const muster = new RegExp(`(^|[^A-ZÄÖÜ0-9_.])${name}\\s*\\(`, "u");
    const treffer = muster.exec(gross);
    if (!treffer) continue;

    let tiefe = 0;
    let inText = false;
    let aktuell = "";
    const argumente: string[] = [];

    for (let i = treffer.index + treffer[0].length; i < formula.length; i += 1) {
      const zeichen = formula[i];

      if (zeichen === '"') {
        inText = !inText;
        aktuell += zeichen;
        continue;
      }
      if (inText) {
        aktuell += zeichen;
        continue;
      }
      if (zeichen === "(") tiefe += 1;
      if (zeichen === ")") {
        if (tiefe === 0) {
          argumente.push(aktuell.trim());
          return argumente;
        }
        tiefe -= 1;
      }
      if ((zeichen === ";" || zeichen === ",") && tiefe === 0) {
        argumente.push(aktuell.trim());
        aktuell = "";
        continue;
      }
      aktuell += zeichen;
    }

    // Klammer nicht geschlossen – die Formel ist unvollständig.
    argumente.push(aktuell.trim());
    return argumente;
  }
  return null;
}
